import { inject, Injectable } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { Meta } from '@df/core/meta/meta';
import { Client } from '@df/core/service/client';
import { StylesService } from '@df/core/service/styles';
import { Subscription } from 'rxjs/internal/Subscription';

@Injectable({
    providedIn: 'root'
})
export class StylesServiceDecorator extends StylesService {
    private _client = inject(Client);
    private _meta = inject(Meta);

    // Performance optimization properties
    private lastScrollTop: number = -1;
    private scrollUpdatePending: boolean = false;
    private rafId: number | null = null;
    private readonly scrollThreshold: number = 1; // Minimum pixels to trigger update

    // Scroll subscription management
    private scrollSubscription?: Subscription;
    private isProductPage: boolean = false;

    override appInitialize() {
        super.appInitialize();
        this.setupProductPageListener();
        this.updateScrollListener();
    }

    /**
     * Setup listener for product page navigation
     */
    private setupProductPageListener(): void {
        this.subscriptions.add(
            this._meta.routerEvents.subscribe(routerEvent => {
                if (routerEvent instanceof NavigationEnd) {
                    this.updateScrollListener();
                }
            })
        );
    }

    /**
     * Update scroll listener based on current page type
     */
    private updateScrollListener(): void {
        const isProductPage = this._meta.model?.data?.type === 'product';

        // Only update if page type changed
        if (this.isProductPage === isProductPage) {
            return;
        }

        this.isProductPage = isProductPage;

        // Unsubscribe from scroll if not on product page
        if (!isProductPage) {
            this.setCssVar('--scrollTop', undefined);
            if (this.scrollSubscription) {
                this.scrollSubscription.unsubscribe();
                this.scrollSubscription = undefined;
            }
            return;
        }

        // Subscribe to scroll only on product pages
        if (!this.scrollSubscription) {
            this.setSCrollTop();
        }
    }

    /**
     * Optimized scroll top tracking with 60fps throttling and caching
     * Overrides the base implementation to provide better performance
     * Only active on product pages
     */
    protected override setSCrollTop() {
        this.scrollSubscription = this._client.obs.scroll.subscribe(() => {
            this.scheduleScrollUpdate();
        });
    }

    /**
     * Schedules a scroll update using requestAnimationFrame for 60fps performance
     * Prevents multiple pending updates and ensures smooth animation
     */
    private scheduleScrollUpdate(): void {
        if (this.scrollUpdatePending) {
            return; // Already scheduled, avoid duplicate RAF calls
        }

        this.scrollUpdatePending = true;

        // Cancel any existing RAF to prevent stacking
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
        }

        this.rafId = requestAnimationFrame(() => {
            this.updateScrollTop();
            this.scrollUpdatePending = false;
            this.rafId = null;
        });
    }

    /**
     * Updates the --scrollTop CSS variable only when the change is significant
     * Minimizes DOM updates and prevents unnecessary reflows/repaints
     */
    private updateScrollTop(): void {
        const currentScrollTop = this._client.getRootElement().scrollTop || 0;

        // Only update if the change exceeds the threshold to avoid excessive DOM updates
        if (Math.abs(currentScrollTop - this.lastScrollTop) >= this.scrollThreshold) {
            this.setCssVar('--scrollTop', `${currentScrollTop}px`);
            this.lastScrollTop = currentScrollTop;
        }
    }

    override ngOnDestroy() {
        // Clean up scroll subscription
        if (this.scrollSubscription) {
            this.scrollSubscription.unsubscribe();
            this.scrollSubscription = undefined;
        }

        // Clean up any pending requestAnimationFrame to prevent memory leaks
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        super.ngOnDestroy();
    }
}
